<template>
  <div class="flex flex-col gap-1">
    <dt :class="valueClass" data-testid="stat-value">
      {{ displayValue }}
    </dt>
    <dd :class="labelClass">{{ label }}</dd>
    <p v-if="helper" :class="helperClass">{{ helper }}</p>
  </div>
</template>

<script setup lang="ts">
/**
 * Одна метрика из `StatsBlock`. Выделена в отдельный компонент,
 * чтобы каждый экземпляр мог владеть своим `useCountUp` без
 * обходного пути с фиксированным MAX=6 в родителе.
 *
 * Стартует анимацию, когда пропс `reveal` становится `true`
 * (родитель прокидывает `IntersectionObserver`-флаг через
 * `useScrollReveal`).
 */
import { computed, toRef, watch } from 'vue';
import { cva } from 'class-variance-authority';
import { useCountUp } from '@/composables/useCountUp';

const valueVariants = cva('font-sans font-extrabold tabular-nums md:text-5xl', {
  variants: {
    tone: {
      default: 'text-ink-strong',
      inverse: 'text-ink-inverse',
      white: 'text-white',
    },
    size: {
      default: 'text-3xl',
      large: 'text-4xl',
    },
  },
  defaultVariants: { tone: 'default', size: 'default' },
});

const labelVariants = cva('', {
  variants: {
    tone: {
      default: 'text-ink-muted',
      inverse: 'text-ink-inverse/70',
      white: 'text-white',
    },
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
  },
  defaultVariants: { tone: 'default', size: 'sm' },
});

interface Props {
  rawValue: string;
  label: string;
  helper?: string;
  tone?: 'default' | 'inverse' | 'white';
  size?: 'default' | 'large';
  duration?: number;
  reveal: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  helper: '',
  tone: 'default',
  duration: 1200,
});

interface ParsedStat {
  readonly target: number;
  readonly prefix: string;
  readonly suffix: string;
  readonly hasNumber: boolean;
}

function parseValue(raw: string): ParsedStat {
  const match = raw.match(/^(\D*)([\d.,]+)(.*)$/);
  if (!match) {
    return { target: 0, prefix: raw, suffix: '', hasNumber: false };
  }
  const [, prefix = '', num = '0', suffix = ''] = match;
  const normalized = num.replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return {
    target: Number.isFinite(parsed) ? parsed : 0,
    prefix,
    suffix,
    hasNumber: Number.isFinite(parsed),
  };
}

const parsed = computed<ParsedStat>(() => parseValue(props.rawValue));
const target = computed(() => parsed.value.target);

const { value, start } = useCountUp(target, { duration: props.duration });

watch(
  toRef(props, 'reveal'),
  (isRevealed) => {
    if (isRevealed) start();
  },
  { immediate: false },
);

const displayValue = computed(() => {
  const stat = parsed.value;
  if (!stat.hasNumber) return props.rawValue;
  const current = Math.round(value.value);
  return `${stat.prefix}${current}${stat.suffix}`;
});

const valueClass = computed(() => valueVariants({ tone: props.tone, size: props.size }));
const labelClass = computed(() => labelVariants({ tone: props.tone, size: 'sm' }));
const helperClass = computed(() => labelVariants({ tone: props.tone, size: 'xs' }));
</script>
